import { CompanyModel } from 'api/model';
import { atom, selector, useRecoilState, useRecoilValue } from 'recoil';
import { useApiCallFn } from 'hooks/useApiCallFn';
import { useCompanyGetCompaniesHook } from 'api/company/company';
import { AXIOS_INSTANCE, handleDates } from 'api/mutator/custom-instance';

const getCompanyAddressDictKey = (address: string, zip: string, city: string) =>
  `${address}${zip}${city}`.toLowerCase().replaceAll(' ', '');

export const companiesAtom = atom<{
  companies: CompanyModel[];
  allCompanies: CompanyModel[];
  companiesById: Map<number, CompanyModel>;
  activeCompaniesByAddrDict: Map<string, CompanyModel>;
  companiesByAddrDict: Map<string, CompanyModel>;
  getCompanyAddressDictKey: (
    address: string,
    zip: string,
    city: string
  ) => string;
}>({
  key: 'companiesLookup',
  default: selector({
    key: 'companiesLookupSelector',
    get: async (): Promise<{
      companies: CompanyModel[];
      allCompanies: CompanyModel[];
      companiesById: Map<number, CompanyModel>;
      activeCompaniesByAddrDict: Map<string, CompanyModel>;
      companiesByAddrDict: Map<string, CompanyModel>;
      getCompanyAddressDictKey: (
        address: string,
        zip: string,
        city: string
      ) => string;
    }> => {
      // Går via samma axios-instans som Orval-hookarna (api/mutator/custom-instance) i stället
      // för rå fetch. Det är inte bara stil: fetch('/api/...') saknar baseURL och fungerar bara
      // så länge appen serveras från samma origin som API:t. AXIOS_INSTANCE bär
      // process.env.API_URL. handleDates körs för att initialdatan ska ha samma typer som
      // useRefreshCompaniesCall producerar — annars skiljer sig samma atom före och efter en
      // refresh. useCustomInstance kan inte användas här: en Recoil-selector är ingen komponent,
      // och namnet use* gör att react-hooks-reglerna slår till.
      let deserialized: CompanyModel[];
      try {
        const { data } = await AXIOS_INSTANCE.post<CompanyModel[]>(
          '/api/Company/GetCompanies'
        );
        handleDates(data);
        deserialized = data;
      } catch {
        // Behåll det tidigare beteendet vid fel: tomma uppslag i stället för ett kastat fel som
        // slår upp i närmaste ErrorBoundary.
        return {
          companies: [],
          allCompanies: [],
          companiesById: new Map(),
          activeCompaniesByAddrDict: new Map(),
          companiesByAddrDict: new Map(),
          getCompanyAddressDictKey,
        };
      }

      const onlyActiveCompanies = deserialized.filter((c) => c.active);

      return {
        companies: onlyActiveCompanies,
        allCompanies: deserialized,
        companiesById: new Map(deserialized.map((e) => [e.companyID, e])),
        activeCompaniesByAddrDict: onlyActiveCompanies.reduce((acc, curr) => {
          // acc[companyAddress] = curr;
          acc.set(
            getCompanyAddressDictKey(curr.address, curr.zip, curr.city),
            curr
          );
          return acc;
        }, new Map<string, CompanyModel>()),
        companiesByAddrDict: deserialized.reduce((acc, curr) => {
          // acc[companyAddress] = curr;
          acc.set(
            getCompanyAddressDictKey(curr.address, curr.zip, curr.city),
            curr
          );
          return acc;
        }, new Map<string, CompanyModel>()),
        getCompanyAddressDictKey,
      };
    },
  }),
});

export const useRefreshCompaniesCall = () => {
  const [, setState] = useRecoilState(companiesAtom);

  const getCompanies = useCompanyGetCompaniesHook();

  const apiCall = useApiCallFn(async () => {
    const result = await getCompanies();
    setState({
      companies: result?.filter((x) => x.active),
      allCompanies: result,
      companiesById: new Map(result.map((comp) => [comp.companyID, comp])),
      activeCompaniesByAddrDict: result
        ?.filter((x) => x.active)
        .reduce((acc, curr) => {
          let companyAddress = `${curr.address}${curr.zip}${curr.city}`;
          companyAddress = companyAddress.toLowerCase().replaceAll(' ', '');
          acc.set(companyAddress, curr);
          return acc;
        }, new Map<string, CompanyModel>()),
      companiesByAddrDict: result.reduce((acc, curr) => {
        let companyAddress = `${curr.address}${curr.zip}${curr.city}`;
        companyAddress = companyAddress.toLowerCase().replaceAll(' ', '');
        acc.set(companyAddress, curr);
        return acc;
      }, new Map<string, CompanyModel>()),
      getCompanyAddressDictKey,
    });
    return result;
  });

  return apiCall;
};

const useCompanies = () => {
  return useRecoilValue(companiesAtom);
};

export default useCompanies;
